import type { BodyNodeLink } from '../types';
import { JOINT_DEFINITIONS, type JointAngles, type JointDefinition } from './JointAngles';

export interface RenderOptions {
  mirror: boolean;
  minScore: number;
  showLabels: boolean;
  showAngles: boolean;
  colorizeByScore: boolean;
  primaryIndex: number;
  angles?: JointAngles;
  jointDefs?: readonly JointDefinition[];
}

export const PERSON_COLORS: readonly string[] = [
  'rgba(98, 217, 255, 0.9)',
  'rgba(255, 183, 77, 0.9)',
  'rgba(122, 255, 160, 0.9)',
  'rgba(255, 119, 217, 0.9)',
  'rgba(192, 128, 255, 0.9)',
];

export class Renderer {
  private readonly ctx: CanvasRenderingContext2D;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.ctx = ctx;
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  clear(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  draw(bodies: readonly BodyNodeLink[], opts: RenderOptions): void {
    const { width, height } = this.canvas;
    this.ctx.save();
    this.ctx.clearRect(0, 0, width, height);

    if (opts.mirror) {
      this.ctx.translate(width, 0);
      this.ctx.scale(-1, 1);
    }

    const multi = bodies.length > 1;
    bodies.forEach((body, i) => {
      const color = PERSON_COLORS[i % PERSON_COLORS.length];
      this.drawLinks(body, opts, width, height, color, multi);
      this.drawNodes(body, opts, width, height, color, multi);
    });

    this.ctx.restore();

    const primary = bodies[opts.primaryIndex];
    if (!primary) return;
    if (opts.showLabels) this.drawLabels(primary, opts, width, height);
    if (opts.showAngles && opts.angles) {
      this.drawAngles(primary, opts, width, height);
    }
  }

  private drawLinks(
    bnl: BodyNodeLink,
    opts: RenderOptions,
    width: number,
    height: number,
    personColor: string,
    multi: boolean,
  ): void {
    this.ctx.lineWidth = 4;
    for (const link of bnl.links) {
      const a = bnl.nodes[link.sourceId];
      const b = bnl.nodes[link.targetId];
      if (!a || !b) continue;
      if (a.score < opts.minScore || b.score < opts.minScore) continue;
      const minScore = Math.min(a.score, b.score);
      this.ctx.strokeStyle =
        !multi && opts.colorizeByScore ? scoreToColor(minScore, 0.7) : personColor;
      this.ctx.beginPath();
      this.ctx.moveTo(a.x * width, a.y * height);
      this.ctx.lineTo(b.x * width, b.y * height);
      this.ctx.stroke();
    }
  }

  private drawNodes(
    bnl: BodyNodeLink,
    opts: RenderOptions,
    width: number,
    height: number,
    personColor: string,
    multi: boolean,
  ): void {
    for (const node of bnl.nodes) {
      if (node.score < opts.minScore) continue;
      const useScoreColor = !multi && opts.colorizeByScore;
      const radius = useScoreColor ? 3 + node.score * 6 : 5;
      this.ctx.fillStyle = useScoreColor ? scoreToColor(node.score, 0.95) : personColor;
      this.ctx.beginPath();
      this.ctx.arc(node.x * width, node.y * height, radius, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }

  private drawLabels(bnl: BodyNodeLink, opts: RenderOptions, width: number, height: number): void {
    this.ctx.save();
    this.ctx.font = '11px ui-monospace, Menlo, monospace';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
    this.ctx.lineWidth = 3;
    for (const node of bnl.nodes) {
      if (node.score < opts.minScore) continue;
      const x = (opts.mirror ? 1 - node.x : node.x) * width + 8;
      const y = node.y * height;
      const text = `${node.name} ${node.score.toFixed(2)}`;
      this.ctx.strokeText(text, x, y);
      this.ctx.fillText(text, x, y);
    }
    this.ctx.restore();
  }

  private drawAngles(bnl: BodyNodeLink, opts: RenderOptions, width: number, height: number): void {
    if (!opts.angles) return;
    const defs = opts.jointDefs ?? JOINT_DEFINITIONS;
    this.ctx.save();
    this.ctx.font = 'bold 13px ui-monospace, Menlo, monospace';
    this.ctx.textBaseline = 'middle';
    this.ctx.textAlign = 'left';
    this.ctx.lineWidth = 3;
    for (const def of defs) {
      const value = opts.angles[def.key];
      if (typeof value !== 'number') continue;
      const b = bnl.nodes[def.b];
      if (!b || b.score < opts.minScore) continue;
      const x = (opts.mirror ? 1 - b.x : b.x) * width + 10;
      const y = b.y * height;
      const text = `${value.toFixed(0)}°`;
      this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
      this.ctx.fillStyle = def.color;
      this.ctx.strokeText(text, x, y);
      this.ctx.fillText(text, x, y);
    }
    this.ctx.restore();
  }
}

function scoreToColor(score: number, alpha: number): string {
  const clamped = Math.max(0, Math.min(1, score));
  const r = Math.round(255 * (1 - clamped));
  const g = Math.round(200 * clamped + 40);
  const b = Math.round(120 * clamped);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
